import { useWBTheme } from "../theme/WBThemeRuntimeProvider";
import type { CSSProperties } from "react";
import { ConsentGate } from "../consent/ConsentGate";
import { type BorderSide, classNames, extractUtilityClasses, getBorderClasses, toCssLength } from "./shared";

/**
 * Audit 11-P0 (CRITICAL stored XSS) fix. The editor-side `getEmbedUrl`
 * allow-listed video hosts before persisting to {@code videoJson};
 * the renderer (this file) did NOT — it merely string-replaced
 * `youtu.be/` and `youtube.com/watch?v=` into embed URLs and dropped
 * the raw value into `<iframe src={normalizedUrl}>`. Any admin-saved
 * `javascript:alert(1)` or `data:text/html,<script>...</script>` would
 * therefore fire on every visitor render.
 *
 * The allow-list mirrors the embed hosts the editor accepts:
 *   - youtube.com / www.youtube.com / youtube-nocookie.com (incl. www.)
 *   - youtu.be
 *   - vimeo.com / player.vimeo.com
 * Scheme is restricted to {@code https:} (and protocol-relative
 * {@code //host/...}, which the URL constructor resolves against
 * https). Anything else returns {@code null} → the iframe is
 * suppressed entirely.
 */
const ALLOWED_VIDEO_HOSTS = new Set<string>([
  "youtube.com",
  "www.youtube.com",
  "youtube-nocookie.com",
  "www.youtube-nocookie.com",
  "youtu.be",
  "vimeo.com",
  "player.vimeo.com",
]);

export function getSafeVideoEmbedUrl(inputUrl: string | undefined | null): string | null {
  if (typeof inputUrl !== "string") return null;
  const trimmed = inputUrl.trim();
  if (trimmed.length === 0) return null;

  // Pre-normalise the YouTube share variants the previous code
  // accepted — but only as a string-replace heuristic. Real
  // validation lives in the URL parse below.
  const candidate = trimmed
    .replace("youtu.be/", "youtube.com/embed/")
    .replace("youtube.com/watch?v=", "youtube.com/embed/");

  let parsed: URL;
  try {
    // Protocol-relative inputs (`//host/...`) resolve against https
    // via the base parameter; anything malformed throws.
    parsed = new URL(candidate, "https://placeholder/");
  } catch {
    return null;
  }

  if (parsed.protocol !== "https:") return null;
  if (!ALLOWED_VIDEO_HOSTS.has(parsed.host.toLowerCase())) return null;

  return parsed.toString();
}

/**
 * youtube.com/embed → youtube-nocookie.com/embed. Fewer cookies once the
 * player runs — NOT a substitute for consent (the IP still goes to Google),
 * which is why the iframe sits behind the ConsentGate either way.
 */
export function toPrivacyEmbedUrl(embedUrl: string): string {
  try {
    const u = new URL(embedUrl);
    const host = u.host.toLowerCase();
    if ((host === "youtube.com" || host === "www.youtube.com") && u.pathname.startsWith("/embed/")) {
      u.host = "www.youtube-nocookie.com";
      return u.toString();
    }
  } catch {
    // already validated upstream; leave untouched
  }
  return embedUrl;
}

export function VideoChild({
  isVisible = true,
  videoUrl,
  url,
  aspectRatio = "aspect-video",
  height = "h-auto",
  align = "center",
  rounded,
  border = false,
  borderColor,
  borderWidth,
  borderStyle,
  borderSides,
}: {
  isVisible?: boolean;
  videoUrl?: string;
  url?: string;
  aspectRatio?: string;
  height?: string;
  align?: "left" | "center" | "right";
  rounded?: string;
  border?: boolean;
  borderColor?: string;
  borderWidth?: string;
  borderStyle?: string;
  borderSides?: BorderSide[];
}) {
  const { currentRounding, currentColorPalette } = useWBTheme();
  const inputUrl = videoUrl ?? url;
  if (!isVisible || !inputUrl) return null;
  // Audit 11-P0: scheme + host allowlist; null suppresses the iframe.
  const normalizedUrl = getSafeVideoEmbedUrl(inputUrl);
  if (normalizedUrl === null) return null;
  const hasFixedHeight = height !== "h-auto";
  const roundedClasses = extractUtilityClasses(
    rounded ?? currentRounding,
    "rounded",
  );
  const aspectRatioClasses = extractUtilityClasses(aspectRatio, "aspect-");
  const heightClasses = extractUtilityClasses(height, "h-");
  const containerAlign =
    align === "left" ? "flex-start" : align === "right" ? "flex-end" : "center";
  const borderCls = getBorderClasses(border, borderWidth || "border", borderStyle || "border-solid", borderSides);
  const mediaClassName = classNames(
    hasFixedHeight ? undefined : "w-full",
    aspectRatioClasses,
    heightClasses,
    borderCls,
    roundedClasses,
  );
  const embedUrl = toPrivacyEmbedUrl(normalizedUrl);
  // ONE style object for iframe and placeholder: consent must not move the page.
  const mediaStyle: CSSProperties = {
    width: hasFixedHeight ? undefined : "100%",
    aspectRatio: aspectRatioClasses ? undefined : "16 / 9",
    height: heightClasses ? undefined : toCssLength(height),
    borderColor: border ? (borderColor ?? currentColorPalette.secondary) : undefined,
    borderRadius: roundedClasses ? undefined : toCssLength(rounded ?? currentRounding),
  };
  return (
    <div
      style={{
        width: "100%",
        display: hasFixedHeight ? "flex" : "block",
        justifyContent: containerAlign,
      }}
    >
      <ConsentGate src={embedUrl} kind="video" className={mediaClassName} style={mediaStyle}>
        <iframe
          className={mediaClassName}
          src={embedUrl}
          title="video"
          style={mediaStyle}
          allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </ConsentGate>
    </div>
  );
}

export function MapChild({
  isVisible = true,
  location,
  zoom = 11,
  mapType = "m",
  aspectRatio = "aspect-video",
  height = "h-auto",
  align = "center",
  rounded,
  border = false,
  borderColor,
  borderWidth,
  borderStyle,
  borderSides,
}: {
  isVisible?: boolean;
  location?: string;
  zoom?: number;
  mapType?: "m" | "k" | "h" | "p";
  aspectRatio?: string;
  height?: string;
  align?: "left" | "center" | "right";
  rounded?: string;
  border?: boolean;
  borderColor?: string;
  borderWidth?: string;
  borderStyle?: string;
  borderSides?: BorderSide[];
}) {
  const { currentRounding, currentColorPalette } = useWBTheme();
  if (!isVisible || !location) return null;
  const encodedLocation = encodeURIComponent(location);
  const src = `https://www.google.com/maps?q=${encodedLocation}&z=${zoom}&t=${mapType}&output=embed`;
  const hasFixedHeight = height !== "h-auto";
  const roundedClasses = extractUtilityClasses(
    rounded ?? currentRounding,
    "rounded",
  );
  const aspectRatioClasses = extractUtilityClasses(aspectRatio, "aspect-");
  const heightClasses = extractUtilityClasses(height, "h-");
  const containerAlign =
    align === "left" ? "flex-start" : align === "right" ? "flex-end" : "center";
  const mapBorderCls = getBorderClasses(border, borderWidth || "border", borderStyle || "border-solid", borderSides);
  const mediaClassName = classNames(
    hasFixedHeight ? undefined : "w-full",
    aspectRatioClasses,
    heightClasses,
    mapBorderCls,
    roundedClasses,
  );
  const mediaStyle: CSSProperties = {
    width: hasFixedHeight ? undefined : "100%",
    aspectRatio: aspectRatioClasses ? undefined : "16 / 9",
    height: heightClasses ? undefined : toCssLength(height),
    borderColor: border ? (borderColor ?? currentColorPalette.secondary) : undefined,
    borderRadius: roundedClasses ? undefined : toCssLength(rounded ?? currentRounding),
  };
  return (
    <div
      style={{
        width: "100%",
        display: hasFixedHeight ? "flex" : "block",
        justifyContent: containerAlign,
      }}
    >
      <ConsentGate src={src} kind="map" className={mediaClassName} style={mediaStyle}>
        <iframe className={mediaClassName} title="map" src={src} style={mediaStyle} />
      </ConsentGate>
    </div>
  );
}
